import React from "react";
import { useNavigate } from "react-router-dom";
import {
  PortalPageShell,
  PortalSection,
  PortalStatusChip,
  RoutingTimeline,
  fmtDate,
  humanTourStatus,
} from "@/pages/portal/PortalShared";
import { usePortal } from "@/pages/portal/PortalContext";

export default function PortalMyTour() {
  const nav = useNavigate();
  const { me, activeTour, docs, routing } = usePortal();
  const shared = routing?.shared || [];
  const submitted = routing?.submitted || null;
  const status = humanTourStatus(activeTour?.status);

  if (!activeTour) {
    return (
      <PortalPageShell
        eyebrow="My Tour"
        title="No tour on file"
        subtitle="Your Account Manager will reach out once your first tour is scheduled."
        testid="page-portal-my-tour"
      />
    );
  }

  const topDocs = (docs || []).slice(0, 3);

  return (
    <div className="max-w-[880px] mx-auto" data-testid="page-portal-my-tour">
      <PortalPageShell
        eyebrow="My Tour"
        title={activeTour.display_name}
        subtitle={`${fmtDate(activeTour.start_date)} → ${fmtDate(activeTour.end_date)}`}
        right={<PortalStatusChip tone={status.tone}>{status.label}</PortalStatusChip>}
      >
        <PortalSection label="Tour Information">
          <dl className="grid grid-cols-2 sm:grid-cols-3 gap-y-4 gap-x-6">
            <div>
              <dt className="text-[11px] uppercase tracking-[0.12em] font-semibold" style={{ color: "var(--fg-muted)" }}>Tour Name</dt>
              <dd className="mt-1 text-[14px]" style={{ color: "var(--fg-primary)" }}>{activeTour.display_name}</dd>
            </div>
            {/* RC4 · Portal ITEM 7 — tour number/ID intentionally NOT
                rendered in the client-facing portal. Client name is
                shown below in the "Client" field. Internal routing
                still uses the tour id via URL params; visible chrome
                does not expose it. */}
            <div>
              <dt className="text-[11px] uppercase tracking-[0.12em] font-semibold" style={{ color: "var(--fg-muted)" }}>Client</dt>
              <dd className="mt-1 text-[14px]" style={{ color: "var(--fg-primary)" }} data-testid="portal-tour-client-name">{me?.display_name || activeTour.customer_name}</dd>
            </div>
            <div>
              <dt className="text-[11px] uppercase tracking-[0.12em] font-semibold" style={{ color: "var(--fg-muted)" }}>Status</dt>
              <dd className="mt-1 text-[14px]" style={{ color: "var(--fg-primary)" }}>{status.label}</dd>
            </div>
            <div>
              <dt className="text-[11px] uppercase tracking-[0.12em] font-semibold" style={{ color: "var(--fg-muted)" }}>Dates</dt>
              <dd className="mt-1 text-[14px]" style={{ color: "var(--fg-primary)" }}>
                {fmtDate(activeTour.start_date)} → {fmtDate(activeTour.end_date)}
              </dd>
            </div>
            {/* Second "Client" tile removed — the client name is now
                surfaced immediately below the Tour Name (see above).
                This deduplicates the client field. */}
          </dl>
        </PortalSection>

        <PortalSection label="Contacts">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <div className="text-[11px] uppercase tracking-[0.12em] font-semibold" style={{ color: "var(--fg-muted)" }}>All Access Contact</div>
              <div className="mt-1 text-[14px]" style={{ color: "var(--fg-primary)" }}>Provided by your Account Manager</div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-[0.12em] font-semibold" style={{ color: "var(--fg-muted)" }}>Tour Manager</div>
              <div className="mt-1 text-[14px]" style={{ color: "var(--fg-primary)" }}>{me?.display_name}</div>
            </div>
          </div>
        </PortalSection>

        <PortalSection label="Routing Summary"
          right={
            <button
              className="text-[12.5px] font-medium"
              style={{ color: "#1B4F8A" }}
              onClick={() => nav("/portal/routing")}
              data-testid="portal-mytour-view-routing"
            >View full routing →</button>
          }
        >
          {shared.length > 0 ? (
            <RoutingTimeline stops={shared.slice(0, 3).map((s) => ({
              date: s.date, city: s.city, state: s.state, venue: s.venue,
            }))} />
          ) : submitted && submitted.stops && submitted.stops.length > 0 ? (
            <>
              <div className="mb-2 text-[11px] uppercase tracking-[0.12em] font-semibold"
                   style={{ color: "var(--fg-muted)" }}>
                Your Submitted Routing · Under Review
              </div>
              <RoutingTimeline stops={submitted.stops.slice(0, 3).map((s) => ({
                date: s.date, city: s.city, state: s.state, venue: s.venue,
              }))} />
            </>
          ) : (
            <div className="text-[13.5px]" style={{ color: "var(--fg-secondary)" }}>
              {submitted
                ? "Routing submitted. Under review by All Access."
                : "No routing submitted yet."}
            </div>
          )}
        </PortalSection>

        <PortalSection label="Documents"
          right={
            <button
              className="text-[12.5px] font-medium"
              style={{ color: "#1B4F8A" }}
              onClick={() => nav("/portal/documents")}
              data-testid="portal-mytour-view-docs"
            >View all →</button>
          }
        >
          {topDocs.length === 0 ? (
            <div className="text-[13.5px]" style={{ color: "var(--fg-secondary)" }}>
              No documents available yet.
            </div>
          ) : (
            <ul className="divide-y" style={{ borderColor: "var(--border-subtle)" }}>
              {topDocs.map((d, i) => (
                <li key={i} className="py-3 flex items-center gap-3" data-testid={`portal-mytour-doc-${i}`}>
                  <div className="uppercase text-[10.5px] tracking-[0.14em] font-semibold w-20"
                       style={{ color: "var(--fg-muted)" }}>{d.kind}</div>
                  <div className="flex-1 text-[13.5px]" style={{ color: "var(--fg-primary)" }}>{d.label}</div>
                  <a
                    href={`${process.env.REACT_APP_BACKEND_URL}${d.pdf_url}`}
                    target="_blank" rel="noreferrer"
                    className="text-[12.5px] font-medium" style={{ color: "#1B4F8A" }}
                    data-testid={`portal-mytour-doc-view-${i}`}
                  >
                    View
                  </a>
                </li>
              ))}
            </ul>
          )}
        </PortalSection>
      </PortalPageShell>
    </div>
  );
}
